import { forwardRef, useImperativeHandle, useRef } from "react";

import styles from "./styles.module.scss";

export type EditorHandle = {
  insertNode: (node: Node, replaceLength?: number) => void;
  getText: () => string;
  getRoot: () => HTMLElement | null;
  getCursorIndex: () => number;
  getCaretCoordinates: () => { top: number; left: number; height: number } | null;
};

export type EditorProps = {
  onInput?: (e: React.FormEvent<HTMLDivElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLDivElement>) => void;
};

export const Editor = forwardRef<EditorHandle, EditorProps>((props, ref) => {
  const divRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(ref, () => ({
    insertNode(node, replaceLength = 0) {
      const selection = window.getSelection();
      if (!selection || !selection.rangeCount) return;

      const range = selection.getRangeAt(0);

      if (replaceLength > 0) {
        const startNode = range.startContainer;
        if (startNode.nodeType === Node.TEXT_NODE) {
          range.setStart(startNode, Math.max(0, range.startOffset - replaceLength));
        }
      }

      range.deleteContents();

      const lastChild = node.nodeType === Node.DOCUMENT_FRAGMENT_NODE ? node.lastChild : node;
      range.insertNode(node);

      if (lastChild) {
        range.setStartAfter(lastChild);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    },

    getText() {
      return divRef.current?.textContent || "";
    },

    getRoot() {
      return divRef.current;
    },

    getCursorIndex() {
      const selection = window.getSelection();
      if (!selection || !selection.anchorNode) return 0;

      const range = selection.getRangeAt(0);
      const preRange = range.cloneRange();
      preRange.selectNodeContents(divRef.current!);
      preRange.setEnd(range.endContainer, range.endOffset);

      return preRange.toString().length;
    },

    getCaretCoordinates() {
      const selection = window.getSelection();
      if (!selection || !selection.rangeCount) return null;
      let range = selection.getRangeAt(0);

      let rect = range.getBoundingClientRect();
      if (rect.width === 0 && rect.height === 0) {
        // Fallback for empty line or empty text
        const span = document.createElement("span");
        span.appendChild(document.createTextNode("\u200b"));
        range.insertNode(span);
        rect = span.getBoundingClientRect();
        span.parentNode?.removeChild(span);
      }

      if (rect.width === 0 && rect.height === 0) return null;

      return { top: rect.top, left: rect.left, height: rect.height };
    },
  }));

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Backspace") {
      const selection = window.getSelection();
      if (selection && selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        let targetSpan: HTMLSpanElement | null = null;

        if (!selection.isCollapsed) {
          const node = range.startContainer;
          if (node.nodeType === Node.ELEMENT_NODE) {
            const selectedChild = node.childNodes[range.startOffset];
            if (selectedChild && selectedChild.nodeName === "SPAN" && range.endOffset === range.startOffset + 1) {
              targetSpan = selectedChild as HTMLSpanElement;
            }
          }
          if (!targetSpan && node.nodeName === "SPAN") {
            targetSpan = node as HTMLSpanElement;
          }
          if (!targetSpan && node.parentNode?.nodeName === "SPAN") {
            targetSpan = node.parentNode as HTMLSpanElement;
          }
        } else {
          const node = range.startContainer;
          if (node.nodeType === Node.TEXT_NODE && range.startOffset === 0) {
            if (node.previousSibling && node.previousSibling.nodeName === "SPAN") {
              targetSpan = node.previousSibling as HTMLSpanElement;
            }
          } else if (node.nodeType === Node.ELEMENT_NODE) {
            const child = node.childNodes[range.startOffset - 1];
            if (child && child.nodeName === "SPAN") {
              targetSpan = child as HTMLSpanElement;
            }
          }
        }

        if (targetSpan && targetSpan.dataset.originalVariable) {
          e.preventDefault();
          const text = targetSpan.textContent || "";
          const textNode = document.createTextNode(text);
          targetSpan.replaceWith(textNode);

          const newRange = document.createRange();
          newRange.setStart(textNode, text.length);
          newRange.collapse(true);
          selection.removeAllRanges();
          selection.addRange(newRange);

          // Yield to event loop to let DOM stabilize before triggering onInput
          setTimeout(() => {
            if (props.onInput) {
              props.onInput(e as any);
            }
          }, 0);
          return;
        }
      }
    }

    if (props.onKeyDown) {
      props.onKeyDown(e);
    }
  };

  return (
    <div
      id="chat-input-editor"
      key={"chat-input-editor"}
      className={styles.inputSection}
      ref={divRef}
      contentEditable="plaintext-only"
      onInput={props.onInput}
      onKeyDown={handleKeyDown}
    />
  );
});